import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useResourceReadiness } from '../../hooks/useResourceReadiness'
import { WORKSPACE_DEFAULT_CHECKLIST_ID, type TimelineElement } from '../../context/TimelineContext'
import type { useRecurringLens } from '../../context/recurringLensContextCore'
import type { useWorkspace } from '../../context/useWorkspace'
import type { ChecklistItem as BoardChecklistItem } from '../../types/schema'
import { checklistBoardSessionKeyFor } from './sessionSnapshot'

export function useBoardCoreState({
  timelineChecklistItems,
  activeWorkspace,
  elements,
  recurringLens,
  breadcrumbChain,
  item,
}: {
  timelineChecklistItems: BoardChecklistItem[]
  activeWorkspace: ReturnType<typeof useWorkspace>['workspaces'][number] | undefined
  elements: TimelineElement[]
  recurringLens: ReturnType<typeof useRecurringLens>
  breadcrumbChain: TimelineElement[]
  item: TimelineElement
}) {
  // Pan-momentum support: track last few move samples during a 'pan' drag so
  // we can fling-scroll the board on pointer-up with a rAF decay loop.
  const panVelocityRef = useRef<Array<{ x: number; y: number; t: number }>>([])
  const panMomentumRafRef = useRef<number>(0)
  const stopPanMomentum = useCallback(() => {
    if (panMomentumRafRef.current) {
      cancelAnimationFrame(panMomentumRafRef.current)
      panMomentumRafRef.current = 0
    }
  }, [])
  useEffect(() => () => stopPanMomentum(), [stopPanMomentum])
  const scrollFrameRef = useRef<number | null>(null)
  const tooltipTouchTimerRef = useRef<number | null>(null)
  const lastChecklistMirrorRef = useRef<string>('')
  const timelineChecklistItemsRef = useRef<BoardChecklistItem[]>(timelineChecklistItems)
  const hasBoardSnapshotRef = useRef(false)
  const boardDocumentIdsRef = useRef<Set<string>>(new Set())
  const deletedSourceIdsRef = useRef<Set<string>>(new Set())
  const weightCommitTimersRef = useRef<Record<string, number>>({})
  const cursorWriteTimerRef = useRef<number | null>(null)
  const latestCursorPayloadRef = useRef<{ canvasX: number; canvasY: number; activeFocusedNodeId: string | null } | null>(null)
  const breakingWireCounterRef = useRef(0)
  const [nodes, setNodes] = useState<BoardChecklistItem[]>([])
  const nodesRef = useRef<BoardChecklistItem[]>(nodes)
  nodesRef.current = nodes
  const resourceReadiness = useResourceReadiness(activeWorkspace, nodes, elements)
  const [optimisticPositions, setOptimisticPositions] = useState<Record<string, { x: number; y: number }>>({})
  const optimisticPositionsRef = useRef<Record<string, { x: number; y: number }>>(optimisticPositions)
  optimisticPositionsRef.current = optimisticPositions
  // optimisticWeights is read-only after the bubble's weight dial was removed
  // (the drawer now owns weight editing). Kept so any in-flight optimistic
  // values from older code paths still merge into displayNodes; intentionally
  // no setter is exported.
  const [optimisticWeights] = useState<Record<string, number>>({})
  // ---- Local-first session model (positions + connections) ----------------
  // No real-time on the canvas: data loads once per canvas identity and is
  // then edited locally until the user saves or reloads/reopens the page.
  // Drag positions and wire (prerequisite) edits live in `nodes` until an
  // editor presses Save; baseline is the last DB-synced snapshot.
  const baselineNodesRef = useRef<BoardChecklistItem[]>([])
  const undoStackRef = useRef<BoardChecklistItem[][]>([])
  /** Ids of previously-persisted nodes (gates today, anything in future) that
   * were deleted locally and need a `batch.delete` on the next Save. Without
   * this, persistBoardSnapshot only iterates the surviving snapshot, leaving
   * the orphan Firestore doc behind — so the gate "comes back" after reopen. */
  const pendingDeletionsRef = useRef<Set<string>>(new Set())
  const pendingCloseActionRef = useRef<(() => void) | null>(null)
  const pendingOpenChecklistItemIdRef = useRef<string | null>(null)
  const [isBoardDirty, setIsBoardDirty] = useState(false)
  const isBoardDirtyRef = useRef<boolean>(isBoardDirty)
  isBoardDirtyRef.current = isBoardDirty
  const [canUndoBoard, setCanUndoBoard] = useState(false)
  const [closeBoardDialogOpen, setCloseBoardDialogOpen] = useState(false)
  const [dialogBusyAction, setDialogBusyAction] = useState<null | 'close-save' | 'close-discard' | 'gate-save'>(null)
  const [closeDialogError, setCloseDialogError] = useState<string | null>(null)
  const [gateDialogError, setGateDialogError] = useState<string | null>(null)
  const sessionSnapshotScopeId = recurringLens
    ? (breadcrumbChain[breadcrumbChain.length - 1]?.id ?? WORKSPACE_DEFAULT_CHECKLIST_ID)
    : item.id
  const boardSessionSnapshotKey = useMemo(
    () => checklistBoardSessionKeyFor(
      item.workspaceId,
      sessionSnapshotScopeId,
      recurringLens?.mode,
      recurringLens?.focusedKey,
    ),
    [item.workspaceId, recurringLens?.focusedKey, recurringLens?.mode, sessionSnapshotScopeId],
  )
  /** "You cannot leave a Gate open" dialog. `intent` decides what runs after
   * the user accepts "Remove all open gates and Save anyways": Save trigger
   * saves & keeps the canvas open; Close trigger saves & closes. */
  const [openGatesDialog, setOpenGatesDialog] = useState<{ intent: 'save' | 'close' } | null>(null)
  // Popup-first create flow (P1): when set, a second NodeDetailDrawer opens
  // in isNewNode mode with this placeholder as the working node. On Save the
  // placeholder is committed via `persistDraftChecklist`; on Discard/close
  // it's simply dropped. Firestore is not touched until Save.
  const [draftDrawerNode, setDraftDrawerNode] = useState<BoardChecklistItem | null>(null)

  return {
    panVelocityRef,
    panMomentumRafRef,
    stopPanMomentum,
    scrollFrameRef,
    tooltipTouchTimerRef,
    lastChecklistMirrorRef,
    timelineChecklistItemsRef,
    hasBoardSnapshotRef,
    boardDocumentIdsRef,
    deletedSourceIdsRef,
    weightCommitTimersRef,
    cursorWriteTimerRef,
    latestCursorPayloadRef,
    breakingWireCounterRef,
    nodes,
    setNodes,
    nodesRef,
    resourceReadiness,
    optimisticPositions,
    setOptimisticPositions,
    optimisticPositionsRef,
    optimisticWeights,
    baselineNodesRef,
    undoStackRef,
    pendingDeletionsRef,
    pendingCloseActionRef,
    pendingOpenChecklistItemIdRef,
    isBoardDirty,
    setIsBoardDirty,
    isBoardDirtyRef,
    canUndoBoard,
    setCanUndoBoard,
    closeBoardDialogOpen,
    setCloseBoardDialogOpen,
    dialogBusyAction,
    setDialogBusyAction,
    closeDialogError,
    setCloseDialogError,
    gateDialogError,
    setGateDialogError,
    sessionSnapshotScopeId,
    boardSessionSnapshotKey,
    openGatesDialog,
    setOpenGatesDialog,
    draftDrawerNode,
    setDraftDrawerNode,
  }
}
